import React, { useState, useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APP_CONFIG } from "../config/app.config";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { firebaseUser, loading, refreshAuthState } = useAuth();
    const location = useLocation();
    const [isCheckingVerification, setIsCheckingVerification] = useState(false);
    const [hasCheckedVerification, setHasCheckedVerification] = useState(false);

    // Auto-reload user state when component mounts if email verification is required
    useEffect(() => {
        if (firebaseUser && APP_CONFIG.REQUIRE_EMAIL_VERIFICATION && !firebaseUser.emailVerified && !hasCheckedVerification) {
            setIsCheckingVerification(true);
            refreshAuthState()
                .then(() => {
                    setHasCheckedVerification(true);
                })
                .catch((error) => {
                    console.error("Error refreshing auth state:", error);
                    setHasCheckedVerification(true);
                })
                .finally(() => {
                    setIsCheckingVerification(false);
                });
        } else if (firebaseUser) {
            setHasCheckedVerification(true);
        }
    }, [firebaseUser, hasCheckedVerification, refreshAuthState]);

    if (loading || isCheckingVerification) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    if (!firebaseUser) {
        // Redirect to sign in page with return URL
        return <Navigate to="/signin" state={{ from: location }} replace />;
    }

    // Check email verification if required and we've completed the verification check
    if (APP_CONFIG.REQUIRE_EMAIL_VERIFICATION && !firebaseUser.emailVerified && hasCheckedVerification) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
                <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Email Verification Required</h2>
                    <p className="text-gray-600 mb-4">
                        Please verify your email address to access this page. Check your inbox for a verification email.
                    </p>
                    <div className="flex justify-between">
                        <button
                            onClick={() => window.location.href = '/signin'}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                            Back to Sign In
                        </button>
                        <button
                            onClick={() => window.location.href = '/signin'}
                            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
                        >
                            Resend Verification Email
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return <>{children}</>;
};

export default ProtectedRoute;
